import { Box, Grid, IconButton, InputAdornment, TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import React, { useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { InputProps } from '../../types';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Api } from '../../helpers';
import { Connect, mapDispatchToProps, mapStateToProps } from '../../Redux';
import { UserProps } from '../../types/user';
import eventBus from '../../configs/eventsBus';
import CodeVerification from '../common/codeVerification';
import Show from '../common/show';




interface EditprofileProps {
    User?: UserProps
    setRedux?: (data: any) => void
}

// 'form' - editing Username/Phone/Till Number.
// 'verifyPhone' - only entered if Phone actually changed (updateUserInfo's own verifyPhone
// response) - an SMS code must be confirmed before the change can apply.
// 'enterPin' - every save, phone changed or not, needs this - the server's updateUserInfo always
// comes back with either verifyPhone or verifyPin (never a bare success), same contract
// SpotPoolApp's own Profile/edit.tsx already goes through.
type Step = 'form' | 'verifyPhone' | 'enterPin'

interface StateProps {
    submitting: boolean,
    inputs: InputProps[],
    country: {
        flag: string,
        code: string
    },
    step: Step
    pendingObj: Record<string, any> | null
    pendingCode: string | null
    pinValue: string
    pinError: string | null
}

// A server error comes back in one of a few different shapes depending which action handled
// it ({field,message}, {error:true}+message, or a bare error string) - this just normalizes
// whichever one shows up into a single display string.
const errorMessage = (resp: any): string => {
    if (resp?.error && typeof resp.error === 'object') return resp.error.message || 'Something went wrong.'
    if (typeof resp?.error === 'string') return resp.error
    return resp?.message || 'Something went wrong.'
}

const Editprofile: React.FC<EditprofileProps> = ({ User, setRedux }) => {
    const navigate = useNavigate();
    const callingCode = (User?.Phone ? parsePhoneNumber(User.Phone)?.countryCallingCode : null) as string || '254'

    const initialInputs: InputProps[] = [
        { label: 'Username', value: User?.Username || '', field: 'Username' },
        { label: 'Phone', value: User?.Phone?.replace(`+${callingCode}`, '') || '', field: 'Phone' },
        { label: 'Till Number', value: User?.tillNumber || '', field: 'tillNumber' }
    ]

    const [state, setState] = useState<StateProps>({
        inputs: initialInputs,
        submitting: false,
        country: {
            flag: '🇰🇪',
            code: callingCode
        },
        step: 'form',
        pendingObj: null,
        pendingCode: null,
        pinValue: '',
        pinError: null
    })

    const { inputs, submitting, step, pendingObj, pinValue, pinError } = state;

    const handleOnChange = (value: string, country: any) => {
        setState(s => ({
            ...s,
            country: {
                flag: country.countryCodeEmoji,
                code: country.dialCode
            }
        }));
    };

    const changeInput = (field: string, obj: any) => {
        setState(s => ({
            ...s,
            inputs: s.inputs.map(i => i.field === field ? { ...i, ...obj } : i),
            submitting: false
        }))
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const obj: any = {}
        data.forEach((value, key) => {
            const trimmed = typeof value === 'string' ? value.trim() : value
            obj[key] = key === 'Phone' ? ('+' + parseInt(state.country.code) + parseInt(String(trimmed))) : trimmed
        })

        if (!obj.Username)
            return changeInput('Username', { error: 'Username is required.' })
        if (!inputs.find(i => i.field === 'Phone')?.value)
            return changeInput('Phone', { error: 'Please enter a valid phone number.' })

        setState(s => ({ ...s, submitting: true }))
        Api({ controller: 'Users', action: 'updateUserInfo', userId: User?._id, ...obj }).then((resp: any) => {
            const { error, verifyPin, verifyPhone } = resp
            if (error) {
                setState(s => ({ ...s, submitting: false }))
                if (error.field) changeInput(error.field, { error: error.message })
                else eventBus.dispatch('routes', { action: 'toastMsg', message: errorMessage(resp), type: 'error' })
            } else if (verifyPhone) {
                setState(s => ({ ...s, step: 'verifyPhone', pendingObj: obj, submitting: false }))
            } else if (verifyPin) {
                setState(s => ({ ...s, step: 'enterPin', pendingObj: obj, submitting: false }))
            } else {
                setState(s => ({ ...s, submitting: false }))
            }
        }).catch(() => {
            setState(s => ({ ...s, submitting: false }))
            eventBus.dispatch('routes', { action: 'toastMsg', message: 'Something went wrong. Please try again.', type: 'error' })
        })
    }

    // CodeVerification's own verify() already confirmed the SMS code itself is right (against
    // the 'phoneVerification' action) before calling this - this second call re-runs
    // checkUserDoesNotExist (Username/Phone still free) and always comes back wanting the PIN
    // too, same two-step chain SpotPoolApp's edit.tsx uses.
    const onPhoneVerified = (code: string) => {
        Api({ controller: 'Users', action: 'updateAfterPhoneVeified', userId: User?._id, ...pendingObj, code }).then((resp: any) => {
            const { error, verifyPin } = resp
            if (error) {
                setState(s => ({ ...s, step: 'form' }))
                if (error.field) changeInput(error.field, { error: error.message })
                else eventBus.dispatch('routes', { action: 'toastMsg', message: errorMessage(resp), type: 'error' })
            } else if (verifyPin) {
                setState(s => ({ ...s, step: 'enterPin', pendingCode: code }))
            }
        }).catch(() => eventBus.dispatch('routes', { action: 'toastMsg', message: 'Something went wrong. Please try again.', type: 'error' }))
    }

    const submitPin = (pin: string) => {
        Api({
            controller: 'Users', action: 'updateWithPinSet', userId: User?._id,
            ...pendingObj, code: state.pendingCode, pin
        }).then((resp: any) => {
            const { error, message, User: updatedUser } = resp
            if (error)
                setState(s => ({ ...s, pinError: errorMessage(resp), pinValue: '' }))
            else {
                if (setRedux) setRedux({ User: { ...User, ...updatedUser } })
                eventBus.dispatch('routes', { action: 'toastMsg', message, type: 'success' })
                navigate(-1)
            }
        }).catch(() => setState(s => ({ ...s, pinError: 'Something went wrong. Please try again.', pinValue: '' })))
    }

    const closePinDialog = () => setState(s => ({ ...s, step: 'form', pinValue: '', pinError: null }))

    return (
        <Show>
            <Show.When isTrue={step === 'verifyPhone'}>
                <CodeVerification
                    loginWith='Phone'
                    verifyPage='phoneVerification'
                    userId={User?._id}
                    Value={pendingObj?.Phone}
                    goBack={() => setState(s => ({ ...s, step: 'form' }))}
                    VerifyCode={onPhoneVerified}
                />
            </Show.When>
            <Show.Else>
                <Box sx={{ marginX: 3 }}>

                    <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 11, marginTop: 3 }}>
                        <IconButton onClick={() => navigate(-1)}>
                            <ArrowBackOutlinedIcon />
                        </IconButton>

                        <Typography variant='h5' sx={{ fontWeight: 'bold' }}>Edit Profile</Typography>

                    </Box>

                    <Box component='form' onSubmit={onSubmit}>
                        <Grid container spacing={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 26 }}>
                            {inputs.map((input, i: number) => {
                                const { label, value, error, field, type } = input
                                return (
                                    <Grid
                                        item
                                        xs={12}

                                        key={field}
                                    >
                                        <TextField
                                            variant='standard'
                                            autoComplete="given-name"
                                            name={field}
                                            required
                                            fullWidth
                                            label={label}
                                            type={field === 'phone' ? 'tel' : 'text'}
                                            defaultValue={value}
                                            autoFocus={i === 0}
                                            error={Boolean(error)}
                                            helperText={error}
                                            onFocus={() => changeInput(field, { error: null })}
                                            InputProps={{
                                                startAdornment: (
                                                    field === 'Phone' && (
                                                        <InputAdornment position="start">
                                                            <PhoneInput
                                                                country={'ke'}
                                                                value={state.country.code}
                                                                onChange={(value, country) => handleOnChange(value, country)}

                                                                inputStyle={{ border: 'none', outline: 'none', width: 85, backgroundColor: 'transparent' }}
                                                                dropdownStyle={{ top: 'auto', bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'start', width: 380 }}
                                                                buttonStyle={{
                                                                    backgroundColor: 'transparent', border: 'none', transition: 'none',
                                                                }}

                                                            />



                                                        </InputAdornment>)
                                                ),


                                            }}

                                            sx={{ backgroundColor: 'transparent' }}

                                        />
                                    </Grid>
                                )
                            })}
                        </Grid>

                        <LoadingButton
                            type="submit"
                            variant="contained"
                            sx={{ mt: 4, textTransform: 'none', borderRadius: 30, paddingX: 5 }}
                            loading={submitting}
                        >
                            Update
                        </LoadingButton>
                    </Box>

                </Box>

                <Dialog open={step === 'enterPin'} onClose={closePinDialog} fullWidth maxWidth='xs'>
                    <DialogTitle sx={{ fontWeight: 'bold' }}>Enter PIN</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: 14, color: '#6b7280', mb: 2 }}>
                            Confirm your PIN to save these changes.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            variant='standard'
                            type='password'
                            placeholder='Enter PIN'
                            inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                            value={pinValue}
                            error={Boolean(pinError)}
                            helperText={pinError}
                            onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '').slice(0, 4)
                                setState(s => ({ ...s, pinValue: v, pinError: null }))
                                if (v.length === 4) submitPin(v)
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={closePinDialog}>Cancel</Button>
                    </DialogActions>
                </Dialog>
            </Show.Else>
        </Show>
    )
}

export default Connect(mapStateToProps, mapDispatchToProps)(Editprofile)
